import { HelperProfile } from './helpers.js';
import { BookingSystem } from './booking.js';
import { db, auth } from './firebase-config.js';
import { ApplicationSystem } from './applications.js';
import { formatDate, sanitizeContent, sanitizeContentSync, Validator, toast, Loading } from './utils.js';
import errorHandler from './error-handler.js';

// Helper function to safely convert Firestore timestamps to dates
function safeToDate(timestamp) {
    if (!timestamp) return null;
    
    // Handle Firestore Timestamp
    if (typeof firebase !== 'undefined' && timestamp instanceof firebase.firestore.Timestamp) {
        return timestamp.toDate();
    }
    
    // Already a Date object
    if (timestamp instanceof Date) {
        return timestamp;
    }
    
    // Has a toDate method (Timestamp-like object)
    if (typeof timestamp === 'object' && typeof timestamp.toDate === 'function') {
        try {
            return timestamp.toDate();
        } catch (e) {
            console.warn('Error converting timestamp:', e);
            return null;
        }
    }
    
    // Try to parse as a date string or number
    const parsed = new Date(timestamp);
    return isNaN(parsed.getTime()) ? null : parsed;
}

// Global UI state
const currentSlotId = null;
const currentHelperId = null;
let currentBookingId = null;
let selectedRating = 0;

// Navigation functions
function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

function toggleMobileMenu() {
    const navLinks = document.getElementById('navLinks');
    const menuToggle = document.getElementById('mobileMenuToggle');
    
    navLinks.classList.toggle('active');
    menuToggle.classList.toggle('active');
    
    // Update ARIA attributes
    const isExpanded = navLinks.classList.contains('active');
    menuToggle.setAttribute('aria-expanded', isExpanded);
    
    // Close menu when clicking outside
    if (navLinks.classList.contains('active')) {
        document.addEventListener('click', closeMobileMenuOnClickOutside);
    } else {
        document.removeEventListener('click', closeMobileMenuOnClickOutside);
    }
}

function closeMobileMenuOnClickOutside(event) {
    const navLinks = document.getElementById('navLinks');
    const menuToggle = document.getElementById('mobileMenuToggle');
    
    if (!navLinks.contains(event.target) && !menuToggle.contains(event.target)) {
        navLinks.classList.remove('active');
        menuToggle.classList.remove('active');
        document.removeEventListener('click', closeMobileMenuOnClickOutside);
    }
}

function showHelpers() {
    document.getElementById('helpers').style.display = 'block';
    document.getElementById('helpers').scrollIntoView({ behavior: 'smooth' });
    loadHelpers();
}

// Modal functions
function openAuthModal() {
    document.getElementById('authModal').style.display = 'block';
}

function closeAuthModal() {
    document.getElementById('authModal').style.display = 'none';
}

async function openDashboard() {
    document.getElementById('userDashboard').style.display = 'flex';
    
    // Detect user role and show appropriate content
    if (window.currentUser) {
        try {
            const userDoc = await db.collection('users').doc(window.currentUser.uid).get();
            if (userDoc.exists) {
                const userData = userDoc.data();
                
                // Admin - show link to admin panel
                if (userData.isAdmin) {
                    showAdminDashboard();
                }
                // Helper - show profile completion and sessions
                else if (userData.isHelper) {
                    showHelperDashboard();
                    showTab('profile'); // Default to profile tab for helpers
                }
                // Seeker - show how to get help
                else {
                    showSeekerDashboard();
                    showTab('sessions'); // Default to sessions tab for seekers
                }
            }
        } catch (error) {
            console.error('Error loading user role:', error);
        }
    }
    
    // Legacy booking system disabled - using Cal.com now (2025-07-04)
    // loadUserSessions();
    loadUserProfile();
    loadMySlots();
}

function closeDashboard() {
    document.getElementById('userDashboard').style.display = 'none';
}

function closeHelperModal() {
    document.getElementById('helperDetailModal').style.display = 'none';
}

function closeBookingModal() {
    document.getElementById('bookingModal').style.display = 'none';
}

function closeFeedbackModal() {
    document.getElementById('feedbackModal').style.display = 'none';
}

// Open Cal.com booking in modal
function openCalBooking(calUrl, helperName) {
    const modal = document.getElementById('calBookingModal');
    const content = document.getElementById('calBookingContent');
    
    // Extract username from Cal.com URL
    const username = calUrl.split('/').pop();
    
    // Clear previous content
    content.innerHTML = '';
    
    // Initialize Cal.com embed
    if (window.Cal) {
        window.Cal('inline', {
            elementOrSelector: '#calBookingContent',
            calLink: username,
            config: {
                layout: 'month_view',
                theme: 'light'
            }
        });
        
        modal.style.display = 'block';
    } else {
        // Fallback to opening in new tab if Cal.com script fails
        window.open(calUrl, '_blank');
    }
}

// Close Cal.com modal
function closeCalModal() {
    document.getElementById('calBookingModal').style.display = 'none';
    // Clear the content to stop any ongoing Cal.com processes
    document.getElementById('calBookingContent').innerHTML = '';
}

function openCalUrlModal() {
    document.getElementById('calUrlModal').style.display = 'block';
    // Focus the input field for better UX
    const input = document.getElementById('calUrlInput');
    if (input) {
        input.focus();
        // Pre-fill with current URL if exists
        const currentUrl = document.getElementById('userCalendlyUrl')?.textContent;
        if (currentUrl && currentUrl !== 'Loading...' && currentUrl !== 'Not set') {
            input.value = currentUrl;
        }
    }
}

function closeCalUrlModal() {
    document.getElementById('calUrlModal').style.display = 'none';
    // Clear the form
    const form = document.getElementById('calUrlForm');
    if (form) {
        form.reset();
    }
}

// Dashboard role-specific content
function showAdminDashboard() {
    // Add admin-specific welcome message or quick link
    const dashboardHeader = document.querySelector('.dashboard-content h2');
    if (dashboardHeader) {
        dashboardHeader.innerHTML = 'My Dashboard <a href="/admin.html" class="btn-secondary" style="margin-left: 20px; padding: 8px 20px; font-size: 14px;">Go to Admin Panel</a>';
    }
}

function showHelperDashboard() {
    // Check if helper has completed their profile
    const sessionsTab = document.getElementById('sessions-tab');
    if (sessionsTab && !document.getElementById('helper-welcome')) {
        const welcomeDiv = document.createElement('div');
        welcomeDiv.id = 'helper-welcome';
        welcomeDiv.style.cssText = 'background: #e8f4f1; padding: 20px; border-radius: 10px; margin-bottom: 30px;';
        welcomeDiv.innerHTML = `
            <h3 style="color: var(--primary); margin-bottom: 10px;">Welcome Helper! 👋</h3>
            <p>Thank you for joining our community. Make sure your profile is complete to start receiving help requests.</p>
            <div style="margin-top: 15px;">
                <button class="btn-secondary" data-action="show-tab" data-tab="profile" style="margin-right: 10px;">Complete Profile</button>
                <button class="btn-secondary" data-action="show-tab" data-tab="availability">Set Availability</button>
            </div>
        `;
        sessionsTab.insertBefore(welcomeDiv, sessionsTab.firstChild);
    }
}

function showSeekerDashboard() {
    // Add getting started guide for seekers
    const sessionsTab = document.getElementById('sessions-tab');
    if (sessionsTab && !document.getElementById('seeker-guide')) {
        const guideDiv = document.createElement('div');
        guideDiv.id = 'seeker-guide';
        guideDiv.style.cssText = 'background: #f0f9ff; padding: 20px; border-radius: 10px; margin-bottom: 30px;';
        guideDiv.innerHTML = `
            <h3 style="color: var(--primary); margin-bottom: 10px;">How to Get Help 🎯</h3>
            <ol style="margin: 15px 0; padding-left: 20px;">
                <li style="margin-bottom: 10px;">Browse our <a href="#helpers" data-action="close-and-show-helpers" style="color: var(--primary);">helpers directory</a></li>
                <li style="margin-bottom: 10px;">Find an expert in your area of need</li>
                <li style="margin-bottom: 10px;">Click "Schedule a Free Session" on their profile</li>
                <li style="margin-bottom: 10px;">Book a time that works for both of you</li>
            </ol>
            <button class="btn-primary" data-action="close-and-show-helpers">Find Help Now</button>
        `;
        sessionsTab.insertBefore(guideDiv, sessionsTab.firstChild);
    }
}

// Tab management
function showTab(tabName) {
    document.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
    document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));
    
    event.target.classList.add('active');
    document.getElementById(`${tabName}-tab`).classList.add('active');
}

// Auth mode toggle
let isAuthMode = 'signin';
window.isAuthMode = 'signin';
let registrationIntent = null; // 'seeker' or 'helper'
window.registrationIntent = null;
function toggleAuthMode() {
    isAuthMode = isAuthMode === 'signin' ? 'signup' : 'signin';
    window.isAuthMode = isAuthMode;
    
    if (isAuthMode === 'signup') {
        document.getElementById('authTitle').textContent = 'Create Account';
        document.getElementById('authSubtitle').textContent = 'Join our karma network';
        document.getElementById('authButtonText').textContent = 'Sign Up';
        document.getElementById('authNameGroup').style.display = 'block';
        document.getElementById('authToggleText').textContent = 'Already have an account?';
        document.getElementById('authToggleLink').textContent = 'Sign in';
    } else {
        document.getElementById('authTitle').textContent = 'Welcome Back';
        document.getElementById('authSubtitle').textContent = 'Sign in to continue';
        document.getElementById('authButtonText').textContent = 'Sign In';
        document.getElementById('authNameGroup').style.display = 'none';
        document.getElementById('authToggleText').textContent = 'Don\'t have an account?';
        document.getElementById('authToggleLink').textContent = 'Sign up';
    }
}

// Button handlers
function handleSeekerClick() {
    if (window.currentUser) {
        showHelpers();
    } else {
        registrationIntent = 'seeker';
        window.registrationIntent = 'seeker';
        openAuthModal();
    }
}

function handleHelperClick() {
    if (window.currentUser) {
        openHelperApplication();
    } else {
        registrationIntent = 'helper';
        window.registrationIntent = 'helper';
        openAuthModal();
    }
}

// Load helpers
async function loadHelpers(filter = 'all') {
    const helpersGrid = document.getElementById('helpers-grid');
    helpersGrid.innerHTML = Loading.skeletonGrid(6);
    
    try {
        const helpers = await HelperProfile.getAllHelpers(filter);
        
        if (helpers.length === 0) {
            helpersGrid.innerHTML = sanitizeContentSync('<div style="grid-column: 1 / -1; text-align: center; padding: 60px 20px; color: #64748b;"><p>No helpers found in this category.</p></div>');
            return;
        }

        const helperHtml = helpers.map(helper => {
            // Format expertise areas
            let expertiseDisplay = '';
            if (helper.expertiseAreas && helper.expertiseAreas.length > 0) {
                const expertiseLabels = {
                    'career': 'Career',
                    'finance': 'Finance',
                    'legal': 'Legal',
                    'health': 'Health',
                    'mental-health': 'Wellness',
                    'education': 'Education',
                    'tech': 'Technology',
                    'business': 'Business & Startup',
                    'other': 'Other'
                };
                expertiseDisplay = helper.expertiseAreas
                    .map(area => `<span style="display: inline-block; background: #e8f4f1; color: var(--primary); padding: 4px 12px; border-radius: 15px; font-size: 12px; margin: 2px;">${expertiseLabels[area] || area}</span>`)
                    .join('');
            } else if (helper.primaryExpertise && !helper.title) {
                // Fallback for old data - only show if no title
                expertiseDisplay = `<p>${helper.primaryExpertise || 'Expert'}</p>`;
            }
            
            return `
                <div class="helper-card" data-helper-id="${helper.id}">
                    <img src="https://ui-avatars.com/api/?name=${encodeURIComponent(helper.fullName)}&background=F5A623&color=fff" alt="${helper.fullName}">
                    <h3>${helper.fullName}</h3>
                    ${helper.title ? `<p style="margin: 5px 0;">${helper.title}</p>` : ''}
                    <div style="margin: 10px 0; min-height: 30px;">${expertiseDisplay}</div>
                    <div class="karma-badge">⭐ ${helper.karmaPoints} Karma Points</div>
                    <button class="book-button" data-helper-id="${helper.id}">View Profile</button>
                </div>
            `;
        }).join('');
        helpersGrid.innerHTML = sanitizeContentSync(helperHtml);
        
        // Add event delegation for helper cards
        helpersGrid.addEventListener('click', (e) => {
            // Check if clicked on View Profile button
            if (e.target.classList.contains('book-button')) {
                e.stopPropagation();
                const helperId = e.target.getAttribute('data-helper-id');
                if (helperId) {
                    showHelperDetail(helperId);
                }
            }
            // Check if clicked on helper card (but not on button)
            else if (e.target.closest('.helper-card') && !e.target.closest('.book-button')) {
                const helperCard = e.target.closest('.helper-card');
                const helperId = helperCard.getAttribute('data-helper-id');
                if (helperId) {
                    showHelperDetail(helperId);
                }
            }
        });
    } catch (error) {
        console.error('Error loading helpers:', error);
        helpersGrid.innerHTML = sanitizeContentSync('<div style="grid-column: 1 / -1; text-align: center; padding: 60px 20px; color: #ef4444;"><p>Error loading helpers. Please try again.</p></div>');
        toast.error('Failed to load helpers. Please try again.');
    }
}

// Show helper details
async function showHelperDetail(helperId) {
    const modal = document.getElementById('helperDetailModal');
    const content = document.getElementById('helperDetailContent');
    
    try {
        const helperDoc = await db.collection('helpers').doc(helperId).get();
        if (!helperDoc.exists) {
            throw new Error('Helper profile not found');
        }
        const helper = helperDoc.data();
        
        const userDoc = await db.collection('users').doc(helperId).get();
        if (!userDoc.exists) {
            throw new Error('User profile not found');
        }
        const user = userDoc.data();
        
        // Ensure required fields have fallback values
        const fullName = user.fullName || 'Helper';
        const karmaPoints = user.karmaPoints || 0;
        
        const detailHtml = `
            <div style="text-align: center; padding: 40px;">
                <img src="https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=F5A623&color=fff&size=120"
                     alt="${fullName}"
                     style="width: 120px; height: 120px; border-radius: 50%; margin-bottom: 20px;">
                <h2>${fullName}</h2>
                ${helper.title ? `<p style="color: var(--primary); font-size: 18px; margin-bottom: 10px;">${helper.title}</p>` : ''}
                ${(() => {
        // Display expertise areas
        if (helper.expertiseAreas && helper.expertiseAreas.length > 0) {
            const expertiseLabels = {
                'career': 'Career',
                'finance': 'Finance',
                'legal': 'Legal',
                'health': 'Health',
                'mental-health': 'Wellness',
                'education': 'Education',
                'tech': 'Technology',
                'business': 'Business & Startup',
                'other': 'Other'
            };
            return `<div style="margin: 15px 0;">${helper.expertiseAreas
                .map(area => `<span style="display: inline-block; background: #e8f4f1; color: var(--primary); padding: 6px 16px; border-radius: 20px; font-size: 14px; margin: 3px;">${expertiseLabels[area] || area}</span>`)
                .join('')}</div>`;
        } else if (helper.primaryExpertise && !helper.title) {
            // Fallback for old data - only show if no title
            return `<p style="color: var(--primary); font-size: 16px; margin-bottom: 10px;">${helper.primaryExpertise}</p>`;
        }
        return '';
    })()}
                <div class="karma-badge" style="font-size: 16px;">⭐ ${karmaPoints} Karma Points</div>
                
                <div style="text-align: left; margin: 30px 0; padding: 20px; background: #f8fafc; border-radius: 15px;">
                    <h3 style="margin-bottom: 15px;">About</h3>
                    <p style="color: #64748b; line-height: 1.6;">${helper.bio || 'No bio provided yet.'}</p>
                </div>
                
                ${helper.skills ? `
                    <div style="text-align: left; margin: 30px 0; padding: 20px; background: #f8fafc; border-radius: 15px;">
                        <h3 style="margin-bottom: 15px;">Skills & Expertise</h3>
                        <p style="color: #64748b; line-height: 1.6;">${helper.skills}</p>
                    </div>
                ` : ''}
                
                <h3 style="margin-bottom: 20px;">Book a Session</h3>
                ${helper.calendlyUrl ? 
        `<button class="btn-primary" data-action="open-cal-booking" data-cal-url="${helper.calendlyUrl}" data-helper-name="${fullName}" style="padding: 15px 40px; font-size: 18px;">
                        📅 Schedule a Free Session
                    </button>
                    <p style="color: #64748b; margin-top: 15px; font-size: 14px;">
                        Click to view available times and book directly with ${fullName.split(' ')[0]}
                    </p>` :
        '<p style="color: #64748b;">This helper is setting up their scheduling. Please check back soon or contact them directly.</p>'
}
            </div>
        `;
        content.innerHTML = sanitizeContentSync(detailHtml);
        
        modal.style.display = 'block';
    } catch (error) {
        console.error('Error loading helper details:', error);
        toast.error('Error loading helper details. Please try again.');
    }
}

// Book slot - deprecated, using Cal.com now
async function bookSlot(slotId, helperId) {
    console.warn('bookSlot is deprecated. Bookings are now handled through Cal.com.');
    toast.info('Please use the "Schedule a Free Session" button to book through Cal.com.');
}

// Filter helpers
function filterHelpers(category) {
    document.querySelectorAll('.filter-btn').forEach(btn => btn.classList.remove('active'));
    event.target.classList.add('active');
    loadHelpers(category);
}

// Rating system
function setRating(rating) {
    selectedRating = rating;
    document.querySelectorAll('.star').forEach((star, index) => {
        star.classList.toggle('filled', index < rating);
    });
}

// Submit feedback
async function submitFeedback() {
    if (!selectedRating) {
        toast.warning('Please select a rating');
        return;
    }
    
    try {
        const comment = document.getElementById('feedbackComment').value;
        
        await db.collection('feedback').add({
            bookingId: currentBookingId,
            userId: window.currentUser.uid,
            rating: selectedRating,
            comment: comment,
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
        });
        
        await db.collection('bookings').doc(currentBookingId).update({
            status: 'completed',
            completedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
        
        toast.success('Thank you for your feedback!');
        closeFeedbackModal();
        // Legacy booking system disabled - using Cal.com now
        // loadUserSessions();
    } catch (error) {
        console.error('Error submitting feedback:', error);
        toast.error('Error submitting feedback. Please try again.');
    }
}

function showFeedbackModal(bookingId) {
    currentBookingId = bookingId;
    document.getElementById('feedbackModal').style.display = 'block';
}

// Handle booking response
async function handleBookingResponse(bookingId, slotId, accepted) {
    try {
        await BookingSystem.respondToRequest(bookingId, slotId, accepted);
        // Legacy booking system disabled - using Cal.com now
        // loadUserSessions();
    } catch (error) {
        console.error('Error updating booking:', error);
        toast.error('Error updating booking. Please try again.');
    }
}

// Load user data functions
async function loadUserSessions() {
    if (!window.currentUser) return;

    try {
        const seekerBookings = await BookingSystem.getUserBookings(window.currentUser.uid);
        const helperBookings = await BookingSystem.getUserBookings(window.currentUser.uid, 'helper');

        const pending = helperBookings.filter(b => b.status === 'pending');
        const upcoming = seekerBookings.filter(b => b.status === 'accepted');
        const past = seekerBookings.filter(b => b.status === 'completed');

        // Load pending requests
        let pendingHtml = '<p style="color: #64748b;">No pending requests</p>';
        if (pending.length > 0) {
            const cards = await Promise.all(pending.map(async req => {
                const userDoc = await db.collection('users').doc(req.seekerId).get();
                const name = userDoc.exists ? userDoc.data().fullName : 'Seeker';
                return `
                    <div class="session-card">
                        <div>
                            <h4>${name}</h4>
                            <p>${req.date} at ${req.time}</p>
                        </div>
                        <div style="display: flex; gap: 10px;">
                            <button class="btn-primary booking-response-btn" data-booking-id="${req.id}" data-slot-id="${req.slotId}" data-accept="true">Accept</button>
                            <button class="btn-secondary booking-response-btn" data-booking-id="${req.id}" data-slot-id="${req.slotId}" data-accept="false">Reject</button>
                        </div>
                    </div>
                `;
            }));
            pendingHtml = cards.join('');
        }
        document.getElementById('pending-requests').innerHTML = sanitizeContentSync(pendingHtml);
        
        // Add event delegation for booking response buttons
        const pendingContainer = document.getElementById('pending-requests');
        pendingContainer.addEventListener('click', (e) => {
            if (e.target.classList.contains('booking-response-btn')) {
                const bookingId = e.target.getAttribute('data-booking-id');
                const slotId = e.target.getAttribute('data-slot-id');
                const accept = e.target.getAttribute('data-accept') === 'true';
                if (bookingId && slotId) {
                    handleBookingResponse(bookingId, slotId, accept);
                }
            }
        });
        
        // Load upcoming sessions
        const upcomingHtml = upcoming.length === 0 ?
            '<p style="color: #64748b;">No upcoming sessions</p>' :
            upcoming.map(session => `
                <div class="session-card">
                    <div>
                        <h4>Session with ${session.helperName || 'Helper'}</h4>
                        <p>${session.date} at ${session.time}</p>
                        ${session.zoomLink ? `<p>Join: <a href="${session.zoomLink}" target="_blank">Zoom Link</a></p>` : ''}
                    </div>
                    <span class="session-status status-accepted">Confirmed</span>
                </div>
            `).join('');
        document.getElementById('upcoming-sessions').innerHTML = sanitizeContentSync(upcomingHtml);
        
        // Load past sessions
        const pastHtml = past.length === 0 ?
            '<p style="color: #64748b;">No past sessions</p>' :
            past.map(session => `
                <div class="session-card">
                    <div>
                        <h4>Session with ${session.helperName || 'Helper'}</h4>
                        <p>${session.date}</p>
                    </div>
                    <button class="btn-secondary feedback-btn" data-session-id="${session.id}">Leave Feedback</button>
                </div>
            `).join('');
        document.getElementById('past-sessions').innerHTML = sanitizeContentSync(pastHtml);
        
        // Add event delegation for feedback buttons
        const pastContainer = document.getElementById('past-sessions');
        pastContainer.addEventListener('click', (e) => {
            if (e.target.classList.contains('feedback-btn')) {
                const sessionId = e.target.getAttribute('data-session-id');
                if (sessionId) {
                    showFeedbackModal(sessionId);
                }
            }
        });
    } catch (error) {
        console.error('Error loading sessions:', error);
    }
}

async function loadUserProfile() {
    if (!window.currentUser) return;
    
    try {
        const userDoc = await db.collection('users').doc(window.currentUser.uid).get();
        const userData = userDoc.data();
        
        const helperDoc = await db.collection('helpers').doc(window.currentUser.uid).get();
        const helperData = helperDoc.exists ? helperDoc.data() : {};
        
        document.getElementById('profileName').value = userData.fullName || '';
        document.getElementById('profileBio').value = helperData.bio || '';
        
        // Set expertise selections
        const expertiseSelect = document.getElementById('profileExpertise');
        // Clear previous selections
        Array.from(expertiseSelect.options).forEach(option => {
            option.selected = false;
        });
        
        // Set selections based on expertiseAreas or fallback to primaryExpertise
        if (helperData.expertiseAreas && helperData.expertiseAreas.length > 0) {
            // Set multiple selections from expertiseAreas
            Array.from(expertiseSelect.options).forEach(option => {
                if (helperData.expertiseAreas.includes(option.value)) {
                    option.selected = true;
                }
            });
        } else if (helperData.primaryExpertise) {
            // Fallback for old data - select single expertise
            Array.from(expertiseSelect.options).forEach(option => {
                if (option.value === helperData.primaryExpertise) {
                    option.selected = true;
                }
            });
        }
    } catch (error) {
        console.error('Error loading profile:', error);
    }
}

function disableCalUrlButton(disabled) {
    const btn = document.getElementById('updateCalUrlBtn');
    if (btn) {
        btn.disabled = disabled;
    }
}

async function loadMySlots() {
    if (!window.currentUser) return;

    const urlElement = document.getElementById('userCalendlyUrl');
    if (!urlElement) return;

    try {
        const [userSnap, helperSnap] = await Promise.all([
            db.collection('users').doc(window.currentUser.uid).get(),
            db.collection('helpers').doc(window.currentUser.uid).get()
        ]);

        const isHelper = userSnap.exists && userSnap.data().isHelper === true;
        const calendlyUrl = helperSnap.exists ? helperSnap.data().calendlyUrl : '';

        if (!isHelper) {
            urlElement.textContent = 'Complete helper application to add Cal.com URL';
            disableCalUrlButton(true);
        } else if (calendlyUrl) {
            urlElement.textContent = calendlyUrl;
            disableCalUrlButton(false);
        } else {
            urlElement.textContent = 'No Cal.com URL set';
            disableCalUrlButton(false);
        }
    } catch (err) {
        // Most likely a permission issue on helpers collection.
        console.warn('loadMySlots fallback:', err);
        urlElement.textContent = 'No Cal.com URL set';
        disableCalUrlButton(false);
    }
}

async function loadStats() {
    try {
        // Try to get helpers count (this is public)
        const helpersSnapshot = await db.collection('helpers').where('isActive', '==', true).get();
        const helperCount = helpersSnapshot.size;
        
        document.getElementById('helperCount').textContent = helperCount + '+';
        const helperCount2 = document.getElementById('helperCount2');
        if (helperCount2) {
            helperCount2.textContent = helperCount + '+';
        }
    } catch (error) {
        console.error('Error loading helpers count:', error);
        // Set default values
        document.getElementById('helperCount').textContent = '50+';
        const helperCount2 = document.getElementById('helperCount2');
        if (helperCount2) {
            helperCount2.textContent = '50+';
        }
    }
    
    // Try to get karma points (requires authentication)
    try {
        const usersSnapshot = await db.collection('users').get();
        let totalKarma = 0;
        usersSnapshot.forEach(doc => {
            totalKarma += doc.data().karmaPoints || 0;
        });
        
        if (totalKarma > 0) {
            document.getElementById('karmaCount').textContent = totalKarma;
        }
    } catch (error) {
        // This is expected when not authenticated
        console.log('Karma points require authentication');
        // Keep the infinity symbol as default
    }
}

// Helper Application Functions
function openHelperApplication() {
    document.getElementById('helperApplicationModal').style.display = 'block';
    // Check for and load draft if exists
    loadDraft();
    // Start auto-save
    startAutoSave();
}

function closeHelperApplication() {
    document.getElementById('helperApplicationModal').style.display = 'none';
    document.getElementById('helperApplicationForm').reset();
    // Stop auto-save
    stopAutoSave();
    // Hide draft notification
    document.getElementById('draftNotification').style.display = 'none';
    window.currentDraft = null;
}


// Simple validation for security purposes
function validateHelperApplication() {
    // Basic email validation
    const email = document.getElementById('appEmail').value;
    if (!email.includes('@') || !email.includes('.')) {
        toast.warning('Please enter a valid email address');
        return false;
    }
    
    // File size validation for security (5MB max for images, 50MB for videos)
    const fileInputs = [
        { id: 'appVideoUpload', maxSize: 50, types: ['video'] },
        { id: 'appPhoto', maxSize: 5, types: ['image'] }
    ];
    
    for (const fileInput of fileInputs) {
        const input = document.getElementById(fileInput.id);
        if (input && input.files.length > 0) {
            for (const file of input.files) {
                // Check file size
                if (file.size > fileInput.maxSize * 1024 * 1024) {
                    toast.error(`File '${file.name}' exceeds ${fileInput.maxSize}MB limit`);
                    return false;
                }
                
                // Basic file type check
                const fileType = file.type.toLowerCase();
                const isValidType = fileInput.types.some(type => {
                    if (type.includes('/')) {
                        return fileType === type;
                    } else {
                        return fileType.startsWith(type + '/');
                    }
                });
                
                if (!isValidType) {
                    toast.error(`File '${file.name}' is not an allowed file type`);
                    return false;
                }
            }
        }
    }
    
    // Text length validation to prevent spam
    const textFields = [
        { id: 'appCertifications', maxLength: 500 },
        { id: 'appWrittenIntro', maxLength: 2000 }
    ];
    
    for (const field of textFields) {
        const textarea = document.getElementById(field.id);
        if (textarea && textarea.value.length > field.maxLength) {
            toast.warning(`Text in ${field.id.replace('app', '')} exceeds ${field.maxLength} character limit`);
            return false;
        }
    }
    
    // Validate at least one expertise is selected
    const checkedCount = Array.from(document.getElementsByClassName('appExpertise_class'))
  .filter(checkbox => checkbox.checked)
  .length;
   
    if (checkedCount === 0) {
        toast.warning('Please select at least one area of expertise');
        return false;
    }
    
    return true;
}


function toggleIntroMethod() {
    const method = document.getElementById('appIntroMethod').value;
    document.getElementById('videoIntroGroup').style.display = method === 'video' ? 'block' : 'none';
    document.getElementById('writtenIntroGroup').style.display = method === 'written' ? 'block' : 'none';
}

// Draft Management Functions
let autoSaveInterval = null;

function collectApplicationFormData() {
    // Collect current form data (excluding files)
    return {
        basicInfo: {
            fullName: document.getElementById('appFullName').value,
            email: document.getElementById('appEmail').value,
            phone: document.getElementById('appPhone').value,
            title: document.getElementById('appTitle').value,
            experience: document.getElementById('appExperience').value,
            linkedIn: document.getElementById('appLinkedIn').value
        },
        expertise: {
           areas: document.getElementsByClassName('appExpertise_class') ? 
                        Array.from(document.getElementsByClassName('appExpertise_class')).filter(checkbox => checkbox.checked).map(checkbox => checkbox.value) : [],
                    certifications: document.getElementById('appCertifications').value
        },
        background: {
            method: document.getElementById('appIntroMethod').value,
            writtenIntro: document.getElementById('appWrittenIntro') ? 
                document.getElementById('appWrittenIntro').value : null,
            calendlyUrl: document.getElementById('appCalendlyUrl').value,
            website: document.getElementById('appWebsite').value
        },
        // Store file names for reference (not actual files)
        fileReferences: {
            hasVideo: document.getElementById('appVideoUpload').files.length > 0,
            hasPhoto: document.getElementById('appPhoto').files.length > 0
        }
    };
}

async function saveDraft() {
    if (!window.currentUser) {
        toast.warning('Please sign in to save draft');
        return;
    }
    
    try {
        const draftData = collectApplicationFormData();
        const result = await ApplicationSystem.saveDraft(window.currentUser.uid, draftData);
        
        if (result.success) {
            toast.success('Draft saved successfully!');
        } else {
            throw new Error(result.error);
        }
    } catch (error) {
        console.error('Error saving draft:', error);
        toast.error('Failed to save draft. Please try again.');
    }
}

async function loadDraft() {
    if (!window.currentUser) return;
    
    try {
        const result = await ApplicationSystem.getDraft(window.currentUser.uid);
        
        if (result.success && result.draft) {
            // Show draft notification
            const draftNotification = document.getElementById('draftNotification');
            const draftTimestamp = document.getElementById('draftTimestamp');
            
            if (result.draft.lastSaved) {
                const date = safeToDate(result.draft.lastSaved);
                if (date) {
                    draftTimestamp.textContent = formatDate(date);
                } else {
                    draftTimestamp.textContent = 'Draft saved';
                }
            }
            
            draftNotification.style.display = 'block';
            
            // Store draft data for resume
            window.currentDraft = result.draft;
        }
    } catch (error) {
        console.error('Error loading draft:', error);
    }
}

async function resumeDraft() {
    if (!window.currentDraft) return;
    
    try {
        const draft = window.currentDraft;
        
        // Fill basic info
        if (draft.basicInfo) {
            document.getElementById('appFullName').value = draft.basicInfo.fullName || '';
            document.getElementById('appEmail').value = draft.basicInfo.email || '';
            document.getElementById('appPhone').value = draft.basicInfo.phone || '';
            document.getElementById('appTitle').value = draft.basicInfo.title || '';
            document.getElementById('appExperience').value = draft.basicInfo.experience || '';
            document.getElementById('appLinkedIn').value = draft.basicInfo.linkedIn || '';
        }
        
        // Fill expertise
        if (draft.expertise) {
            // Set multi-select expertise areas
            const expertiseSelect = document.getElementsByClassName('appExpertise_class');

if (draft.expertise.areas) {
    Array.from(expertiseSelect).forEach(option => {
        option.checked = draft.expertise.areas.includes(option.value);
    });
}
            document.getElementById('appCertifications').value = draft.expertise.certifications || '';
        }
        
        // Fill background
        if (draft.background) {
            document.getElementById('appIntroMethod').value = draft.background.method || 'video';
            toggleIntroMethod();
            if (document.getElementById('appWrittenIntro') && draft.background.writtenIntro) {
                document.getElementById('appWrittenIntro').value = draft.background.writtenIntro;
            }
            // Load Cal.com and website from new structure
            document.getElementById('appCalendlyUrl').value = draft.background.calendlyUrl || '';
            document.getElementById('appWebsite').value = draft.background.website || '';
        }
        
        // Handle backward compatibility - check old introduction structure
        if (draft.introduction && !draft.background) {
            // Only load from introduction if background doesn't exist (old drafts)
            document.getElementById('appIntroMethod').value = draft.introduction.method || 'video';
            toggleIntroMethod();
            if (document.getElementById('appWrittenIntro') && draft.introduction.writtenIntro) {
                document.getElementById('appWrittenIntro').value = draft.introduction.writtenIntro;
            }
            document.getElementById('appCalendlyUrl').value = draft.introduction.calendlyUrl || '';
            document.getElementById('appWebsite').value = draft.introduction.website || '';
        }
        
        // Show file reference warnings if files were previously selected
        if (draft.fileReferences) {
            const fileWarning = [];
            if (draft.fileReferences.hasVideo) fileWarning.push('intro video');
            if (draft.fileReferences.hasPhoto) fileWarning.push('profile photo');
            
            if (fileWarning.length > 0) {
                toast.info(`Please re-select your ${fileWarning.join(', ')} files`);
            }
        }
        
        // Hide draft notification
        document.getElementById('draftNotification').style.display = 'none';
        
        // Start auto-save
        startAutoSave();
        
        toast.success('Draft loaded successfully!');
    } catch (error) {
        console.error('Error resuming draft:', error);
        toast.error('Failed to load draft. Please try again.');
    }
}

async function discardDraft() {
    if (!window.currentUser) return;
    
    try {
        const result = await ApplicationSystem.deleteDraft(window.currentUser.uid);
        
        if (result.success) {
            document.getElementById('draftNotification').style.display = 'none';
            window.currentDraft = null;
            toast.success('Draft discarded');
        }
    } catch (error) {
        console.error('Error discarding draft:', error);
        toast.error('Failed to discard draft');
    }
}

function startAutoSave() {
    // Clear any existing interval
    if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
    }
    
    // Auto-save every 30 seconds
    autoSaveInterval = setInterval(async () => {
        if (window.currentUser && document.getElementById('helperApplicationModal').style.display === 'block') {
            const draftData = collectApplicationFormData();
            // Only save if there's actual content
            if (draftData.basicInfo.fullName || draftData.basicInfo.email) {
                await ApplicationSystem.saveDraft(window.currentUser.uid, draftData);
                console.log('Auto-saved draft');
            }
        }
    }, 30000); // 30 seconds
}

function stopAutoSave() {
    if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
        autoSaveInterval = null;
    }
}

// Update Cal.com URL function - opens the modal
function updateCalendlyUrl() {
    // Check authentication first
    if (!window.currentUser || !window.currentUser.uid) {
        toast.error('You must be signed in to update your Cal.com URL.');
        return;
    }
    
    // Also check Firebase Auth state as a backup
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
        toast.error('Your session has expired. Please sign in again.');
        return;
    }
    
    // Open the modal
    openCalUrlModal();
}

// Handle Cal.com URL form submission
async function handleCalUrlSubmit(newUrl) {
    // Check authentication again for safety
    if (!window.currentUser || !window.currentUser.uid) {
        toast.error('You must be signed in to update your Cal.com URL.');
        closeCalUrlModal();
        return;
    }
    
    // Trim the input
    const trimmedUrl = newUrl.trim();
    
    // Strict URL validation (backup for HTML5 validation)
    const CAL_URL_REGEX = /^https:\/\/(www\.)?cal\.com\/[A-Za-z0-9_-]+\/?$/;
    if (!CAL_URL_REGEX.test(trimmedUrl)) {
        toast.warning('Please enter a valid Cal.com URL in the format: https://cal.com/your-handle');
        return;
    }
    
    // Get the submit button for loading state
    const submitButton = document.querySelector('#calUrlForm button[type="submit"]');
    
    try {
        // Show loading state
        if (submitButton) {
            Loading.button(submitButton, true);
        }
        
        await db.collection('helpers').doc(window.currentUser.uid).update({
            calendlyUrl: trimmedUrl
        });
        
        toast.success('Cal.com URL updated successfully!');
        
        // Close the modal
        closeCalUrlModal();
        
        // Only reload if the function exists
        if (typeof loadMySlots === 'function') {
            loadMySlots(); // Reload to show new URL
        }
    } catch (error) {
        console.error('Error updating Cal.com URL:', error);
        
        // Provide specific error messages based on error code
        let errorMessage = 'Error updating Cal.com URL. ';
        
        if (error.code === 'permission-denied') {
            errorMessage = 'You do not have permission to update this profile. Please ensure you are updating your own helper profile.';
        } else if (error.code === 'unauthenticated') {
            errorMessage = 'Your session has expired. Please sign in again.';
        } else if (error.code === 'not-found') {
            errorMessage = 'Helper profile not found. Please complete your helper application first.';
        } else if (error.code === 'unavailable') {
            errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (error.message) {
            errorMessage += error.message;
        } else {
            errorMessage += 'Please try again.';
        }
        
        toast.error(errorMessage);
        
        // Use errorHandler for detailed logging and potential error reporting
        errorHandler.handleError(error, 'UPDATE_CAL_URL');
    } finally {
        // Remove loading state
        if (submitButton) {
            Loading.button(submitButton, false);
        }
    }
}

// Toggle password visibility
function togglePasswordVisibility() {
    const passwordInput = document.getElementById('authPassword');
    const toggleButton = document.querySelector('[data-action="toggle-password"]');
    
    if (!passwordInput || !toggleButton) return;
    
    const isPassword = passwordInput.type === 'password';
    passwordInput.type = isPassword ? 'text' : 'password';
    
    // Update button state
    toggleButton.setAttribute('aria-pressed', !isPassword);
    toggleButton.setAttribute('aria-label', isPassword ? 'Hide password' : 'Show password');
    
    // Update icon (if needed)
    const icon = toggleButton.querySelector('.password-toggle-icon');
    if (icon) {
        icon.textContent = isPassword ? '👁️‍🗨️' : '👁️';
    }
}

// Export all functions for use in other modules and global access
export {
    // Navigation and UI
    scrollToTop,
    toggleMobileMenu,
    showHelpers,
    openAuthModal,
    closeAuthModal,
    openDashboard,
    closeDashboard,
    closeHelperModal,
    closeBookingModal,
    closeFeedbackModal,
    openCalBooking,
    closeCalModal,
    openCalUrlModal,
    closeCalUrlModal,
    handleCalUrlSubmit,
    showTab,
    toggleAuthMode,
    togglePasswordVisibility,
    
    // User interaction handlers
    handleSeekerClick,
    handleHelperClick,
    showHelperDetail,
    bookSlot,
    filterHelpers,
    setRating,
    submitFeedback,
    showFeedbackModal,
    handleBookingResponse,
    
    // Helper application functions
    openHelperApplication,
    closeHelperApplication,
    toggleIntroMethod,
    validateHelperApplication,
    
    // Draft management functions
    saveDraft,
    resumeDraft,
    discardDraft,
    loadDraft,
    startAutoSave,
    stopAutoSave,
    
    // Data loading functions 
    loadHelpers, 
    loadStats, 
    loadUserSessions, 
    loadUserProfile, 
    loadMySlots,
    updateCalendlyUrl,
    currentSlotId,
    currentHelperId,
    isAuthMode
};
